import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, Phone, Calendar, HelpCircle } from 'lucide-react';
import { generateFAQPageSchema, generateBreadcrumbSchema } from '../utils/schemaMarkup';
import { useSchemaMarkup } from '../utils/useSchemaMarkup';
import { PhoneLinkText } from '../components/PhoneLinkText';

// All FAQ data for schema generation
const allFaqs = [
  // General Questions
  { question: "What areas do you serve?", answer: "ComfortMaster Heating & Air proudly serves Central Kentucky including Lexington, Nicholasville, Richmond, Georgetown, Frankfort, Winchester, Versailles, Paris, Danville, Harrodsburg, and surrounding areas." },
  { question: "Do you offer 24/7 emergency service?", answer: "Yes! We offer 24/7 emergency HVAC service. If your heating or cooling system breaks down outside of normal business hours, call us at (859) 357-6803 and we will dispatch a technician to help you." },
  { question: "Are you licensed and insured?", answer: "Yes, ComfortMaster Heating & Air is fully licensed (KY License #HM07021) and insured. All of our technicians are certified and undergo continuous training to stay current with the latest HVAC technologies." },
  { question: "Do you offer free estimates?", answer: "Yes! We offer free estimates on new HVAC system installations and replacements. We also provide complimentary second opinions if you have received a quote from another company." },
  // Heating Questions
  { question: "How often should I have my furnace serviced?", answer: "We recommend having your furnace professionally serviced at least once a year, ideally in the fall before the heating season begins. Regular maintenance helps ensure safe operation, improves efficiency, and can prevent costly breakdowns." },
  { question: "Why is my furnace blowing cold air?", answer: "There are several reasons your furnace might blow cold air, including a dirty air filter, thermostat issues, pilot light problems, or a malfunctioning flame sensor. Contact us for a professional diagnosis." },
  { question: "How long does a furnace typically last?", answer: "With proper maintenance, a gas furnace typically lasts 15-20 years. If your furnace is approaching this age and requiring frequent repairs, it may be more cost-effective to replace it with a new, energy-efficient model." },
  { question: "What is included in an AC tune-up?", answer: "Our AC tune-up includes a 21-point inspection, cleaning of coils and components, refrigerant check, safety checks, thermostat calibration, filter replacement, and efficiency testing. Currently offered at $99 (save $40)." },
  // Cooling Questions
  { question: "How often should I replace my AC filter?", answer: "We recommend replacing your AC filter every 1-3 months, depending on factors like pets, allergies, and how often you run your system. A dirty filter restricts airflow and reduces efficiency." },
  { question: "Why is my AC not cooling properly?", answer: "Common causes include low refrigerant levels, a dirty condenser coil, frozen evaporator coils, or a faulty compressor. Our technicians can diagnose and repair the issue quickly." },
  { question: "How long does an AC unit last?", answer: "A well-maintained central air conditioning unit typically lasts 15-20 years. Regular maintenance, including annual tune-ups and filter changes, can help maximize your system lifespan." },
  { question: "What size AC do I need for my home?", answer: "AC sizing depends on many factors including square footage, insulation, windows, and climate. Our technicians perform a load calculation to determine the right size system for your home." },
  // Pricing Questions
  { question: "What payment methods do you accept?", answer: "We accept cash, checks, and all major credit cards including Visa, MasterCard, American Express, and Discover." },
  { question: "Do you provide free estimates?", answer: "Yes! We provide free estimates for new system installations and major repairs. Call us at (859) 357-6803 to schedule your free consultation." },
  { question: "What is the Comfort Loyalty Club?", answer: "The Comfort Loyalty Club is our monthly maintenance membership program ($16/month) that includes two seasonal tune-ups, priority scheduling, 10% discount on repairs, and no overtime charges." }
];

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-stone-200">
      <button
        className="w-full py-5 px-6 flex items-center justify-between text-left"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="font-semibold text-slate-900 pr-8">{question}</span>
        <ChevronDown className={`w-5 h-5 text-emerald-600 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="pb-5 px-6 pr-12">
          <p className="text-slate-600 leading-relaxed"><PhoneLinkText text={answer} /></p>
        </div>
      )}
    </div>
  );
};

const FAQPage = () => {
  // Generate schemas
  const faqSchema = generateFAQPageSchema(allFaqs);
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: 'https://comfortmasterheating.com' },
    { name: 'FAQ', url: 'https://comfortmasterheating.com/faq' }
  ]);

  // Inject schemas into document head
  useSchemaMarkup([faqSchema, breadcrumbSchema], 'faq');

  return (
    <main>
      <Helmet>
        <title>Frequently Asked Questions | ComfortMaster Heating & Air | Lexington KY</title>
        <meta name="description" content="Find answers to common HVAC questions. Learn about furnace maintenance, AC repair, pricing, and services from ComfortMaster Heating & Air in Central Kentucky." />
        <link rel="canonical" href="https://comfortmasterheating.com/faq" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-emerald-400 font-semibold text-sm uppercase tracking-wider">FAQ</span>
            <h1 className="font-heading font-bold text-5xl md:text-6xl mt-4 mb-6">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-stone-300 mb-8">
              Find answers to common questions about our HVAC services, maintenance, and more. Cannot find what you are looking for? Contact us directly.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="btn-primary inline-flex items-center justify-center gap-2" data-testid="faq-contact-btn">
                <Calendar size={20} />
                Contact Us
              </Link>
              <a href="tel:8593576803" className="btn-secondary inline-flex items-center justify-center gap-2 bg-transparent border-white/30 text-white hover:bg-white hover:text-slate-900" data-testid="faq-call-btn">
                <Phone size={20} />
                (859) 357-6803
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-20 bg-stone-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* General Questions */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
                <HelpCircle className="w-5 h-5 text-white" />
              </div>
              <h2 className="font-heading font-bold text-2xl text-slate-900">General Questions</h2>
            </div>
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <FAQItem 
                question="What areas do you serve?" 
                answer="ComfortMaster Heating & Air proudly serves Central Kentucky including Lexington, Nicholasville, Richmond, Georgetown, Frankfort, Winchester, Versailles, Paris, Danville, Harrodsburg, and surrounding areas."
              />
              <FAQItem 
                question="Do you offer 24/7 emergency service?" 
                answer="Yes! We offer 24/7 emergency HVAC service. If your heating or cooling system breaks down outside of normal business hours, call us at (859) 357-6803 and we will dispatch a technician to help you."
              />
              <FAQItem 
                question="Are you licensed and insured?" 
                answer="Yes, ComfortMaster Heating & Air is fully licensed (KY License #HM07021) and insured. All of our technicians are certified and undergo continuous training to stay current with the latest HVAC technologies."
              />
              <FAQItem 
                question="Do you offer free estimates?" 
                answer="Yes! We offer free estimates on new HVAC system installations and replacements. We also provide complimentary second opinions if you have received a quote from another company."
              />
            </div>
          </div>

          {/* Heating Questions */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-orange-600 rounded-lg flex items-center justify-center">
                <HelpCircle className="w-5 h-5 text-white" />
              </div>
              <h2 className="font-heading font-bold text-2xl text-slate-900">Heating Questions</h2>
            </div>
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <FAQItem 
                question="How often should I have my furnace serviced?" 
                answer="We recommend having your furnace professionally serviced at least once a year, ideally in the fall before the heating season begins. Regular maintenance helps ensure safe operation, improves efficiency, and can prevent costly breakdowns."
              />
              <FAQItem 
                question="Why is my furnace blowing cold air?" 
                answer="There are several reasons your furnace might blow cold air, including a dirty air filter, thermostat issues, pilot light problems, or a malfunctioning flame sensor. Contact us for a professional diagnosis."
              />
              <FAQItem 
                question="How long does a furnace typically last?" 
                answer="With proper maintenance, a gas furnace typically lasts 15-20 years. If your furnace is approaching this age and requiring frequent repairs, it may be more cost-effective to replace it with a new, energy-efficient model."
              />
              <FAQItem 
                question="What is included in an AC tune-up?" 
                answer="Our AC tune-up includes a 21-point inspection, cleaning of coils and components, refrigerant check, safety checks, thermostat calibration, filter replacement, and efficiency testing. Currently offered at $99 (save $40)."
              />
            </div>
          </div>

          {/* Cooling Questions */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <HelpCircle className="w-5 h-5 text-white" />
              </div>
              <h2 className="font-heading font-bold text-2xl text-slate-900">Cooling Questions</h2>
            </div>
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <FAQItem 
                question="How often should I replace my AC filter?" 
                answer="We recommend replacing your AC filter every 1-3 months, depending on factors like pets, allergies, and how often you run your system. A dirty filter restricts airflow and reduces efficiency."
              />
              <FAQItem 
                question="Why is my AC not cooling properly?" 
                answer="Common causes include low refrigerant levels, a dirty condenser coil, frozen evaporator coils, or a faulty compressor. Our technicians can diagnose and repair the issue quickly."
              />
              <FAQItem 
                question="How long does an AC unit last?" 
                answer="A well-maintained central air conditioning unit typically lasts 15-20 years. Regular maintenance, including annual tune-ups and filter changes, can help maximize your system lifespan."
              />
              <FAQItem 
                question="What size AC do I need for my home?" 
                answer="AC sizing depends on many factors including square footage, insulation, windows, and climate. Our technicians perform a load calculation to determine the right size system for your home."
              />
            </div>
          </div>

          {/* Pricing Questions */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                <HelpCircle className="w-5 h-5 text-white" />
              </div>
              <h2 className="font-heading font-bold text-2xl text-slate-900">Pricing & Payment</h2>
            </div>
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <FAQItem 
                question="What payment methods do you accept?" 
                answer="We accept cash, checks, and all major credit cards including Visa, MasterCard, American Express, and Discover."
              />
              <FAQItem 
                question="Do you provide free estimates?" 
                answer="Yes! We provide free estimates for new system installations and major repairs. Call us at (859) 357-6803 to schedule your free consultation."
              />
              <FAQItem 
                question="What is the Comfort Loyalty Club?" 
                answer="The Comfort Loyalty Club is our monthly maintenance membership program ($16/month) that includes two seasonal tune-ups, priority scheduling, 10% discount on repairs, and no overtime charges."
              />
            </div>
          </div>

        </div>
      </section>

      {/* Still Have Questions CTA */}
      <section className="py-16 bg-emerald-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <HelpCircle className="w-12 h-12 mx-auto mb-4 text-emerald-200" />
          <h2 className="font-heading font-bold text-3xl mb-4">Still Have Questions?</h2>
          <p className="text-emerald-100 mb-6 max-w-2xl mx-auto">
            Our friendly team is here to help. Contact us today and we will be happy to answer any questions about your heating and cooling needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-white text-emerald-700 font-bold py-3 px-6 rounded-full" data-testid="faq-cta-contact">
              Contact Us
            </Link>
            <a href="tel:8593576803" className="inline-flex items-center justify-center gap-2 bg-emerald-700 text-white font-bold py-3 px-6 rounded-full border-2 border-white/30" data-testid="faq-cta-call">
              <Phone size={18} />
              (859) 357-6803
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default FAQPage;
